import styled, { css } from "styled-components"

import { spacing, colors, typography, borderRadius, elevation } from "@izettle/otto"

import { ModalSize } from "../../../enumerations"
import { Divider } from "../../divider"
import { IconButton } from "../../icon-button"

import {
  ModalBaseBackdropProps,
  ModalBaseContainerProps,
  ModalBaseContentProps,
  ModalBaseHeaderRowProps,
} from "./types"

const MODAL_PADDING_VARIABLE = "--iz-modal-padding"
const setPaddingVariable = ({ isMobile }: { isMobile?: boolean }) => `
  ${MODAL_PADDING_VARIABLE}: ${isMobile ? spacing.small : spacing.medium};
`

export const ModalBaseOverflowingContainer = styled.div`
  overflow-y: scroll;
  display: flex;
  position: absolute;
  align-items: center;
  justify-content: center;
  width: calc(100% - var(${MODAL_PADDING_VARIABLE}) * 2);
  padding: var(${MODAL_PADDING_VARIABLE}) 0;
  top: 0;
  min-height: calc(100% - var(${MODAL_PADDING_VARIABLE}) * 2);
  height: fit-content;
`

export const ModalBaseBackdrop = styled.div<ModalBaseBackdropProps>`
  ${setPaddingVariable}
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  position: fixed;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  /* stylelint-disable-next-line */
  background: rgba(0, 0, 0, 0.2);
  z-index: 9000;
  padding: var(${MODAL_PADDING_VARIABLE});

  &.transition-enter {
    opacity: 0;
    transform: scale(1.01);
  }

  &.transition-enter-active {
    opacity: 1;
    transform: scale(1);
    transition: opacity 0.2s, transform 0.2s;
  }

  &.transition-exit {
    opacity: 1;
    transform: scale(1);
  }

  &.transition-exit-active {
    opacity: 0;
    transform: scale(1.01);
    transition: opacity 0.2s, transform 0.2s;
  }

  ${({ isOverflowing }) =>
    isOverflowing &&
    `
    overflow-y: scroll;
  `}
`

const mixinContainerSize = ({ size, isMobile }: ModalBaseContainerProps) => {
  if (isMobile) {
    return css`
      position: fixed;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      height: 100%;
      width: 100%;
      box-shadow: none;
      border-radius: 0;
      padding-bottom: ${spacing.small};
    `
  }
  switch (size) {
    case ModalSize.Small:
      return css`
        width: 400px;
        min-height: 0;
        max-height: 400px;
      `
    case ModalSize.Large:
      return css`
        width: 800px;
        min-height: 0;
        max-height: 600px;
      `
    case ModalSize.Medium:
    default:
      return css`
        width: 600px;
        min-height: 0;
        max-height: 600px;
      `
  }
}

export const ModalBaseContainer = styled.div<ModalBaseContainerProps>`
  background: ${colors.background.default};
  border-radius: ${borderRadius.large};
  display: flex;
  box-shadow: ${elevation.level4};
  flex-direction: column;
  max-width: 100%;
  position: relative;
  overflow: hidden;
  padding-bottom: ${({ hasFooter }) =>
    hasFooter ? spacing.medium : `calc(${spacing.medium} + ${spacing.small})`};

  ${mixinContainerSize};

  ${({ isOverflowing }) =>
    isOverflowing &&
    `
    max-height: initial;
  `}
`

export const ModalBaseContent = styled.div<ModalBaseContentProps>`
  min-height: 0;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0 var(${MODAL_PADDING_VARIABLE});
  ${({ hideTextLabel }) =>
    hideTextLabel ? `padding-top: calc(${spacing.large} + ${spacing.xxSmall});` : ""}
`

export const ModalBaseHeader = styled.div<ModalBaseHeaderRowProps>`
  min-height: fit-content;
  padding-right: calc(${spacing.large} * 2);

  ${({ hideTextLabel }) =>
    hideTextLabel
      ? css`
          visibility: hidden;
          position: absolute;
        `
      : css`
          visibility: visible;
        `};
`

export const ModalBaseHeaderRow = styled.div<ModalBaseHeaderRowProps>`
  width: 100%;
  padding: ${spacing.medium};
  padding: var(${MODAL_PADDING_VARIABLE});
`

export const ModalBaseLabel = styled.p`
  margin: 0 ${spacing.large} 0 0;
  align-items: center;
  display: flex;
  ${typography.regularBold}
`

const getIconPosition = ({ isMobile }: { isMobile?: boolean }) =>
  isMobile ? spacing.small : `calc(${spacing.small} + ${spacing.tiny})`

export const StyledIconButton = styled(IconButton)<{ isMobile?: boolean }>`
  position: absolute;
  top: ${getIconPosition};
  right: ${getIconPosition};
`

export const ModalBaseEndFocus = styled.div`
  height: 0;
`

export const StyledDividerHeader = styled(Divider)`
  margin: 0;
`
